import React from 'react';
import { TEXT } from '../../config/listingPerformanceLabels';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import { Figure, OccupancyCell, Hint } from './parts';

/**
 * «أداء الوحدات» — «كل المنصات»: the PMS's own week for every unit (the Monday snapshot) — occupancy,
 * the nights behind it (booked · open · closed · unknown), bookings made per platform and new Airbnb
 * reviews. Gathern has no platform funnel, so this is where it shows (plan §2.14).
 * "Unknown" nights have no calendar record — they are never shown as open or closed.
 */
const platformsText = (made) => Object.entries(made || {})
  .filter(([, n]) => n > 0)
  .map(([p, n]) => `${getPlatformDisplayName(p)} ${n}`)
  .join('، ');

const AllPlatformsTable = ({ rows = [], ownerView, selected, onSelect }) => {
  if (!rows.length) return <p className="lp-empty">{TEXT.noRows}</p>;
  const nightCols = ownerView ? 1 : 4;
  return (
    <div className="lp-scroll">
      <table className="lp-table lp-all">
        <thead>
          <tr className="lp-groups" aria-hidden="true">
            <th />
            <th className="lp-group-start" />
            <th colSpan={nightCols} className="lp-group-start">{TEXT.groups.nights}</th>
            <th colSpan={2} className="lp-group-start">{TEXT.groups.week}</th>
          </tr>
          <tr>
            <th scope="col">{TEXT.unit}</th>
            <th scope="col" className="lp-group-start">{TEXT.occupancy}</th>
            <th scope="col" className="lp-group-start">{TEXT.bookedNights}</th>
            {!ownerView && <th scope="col">{TEXT.openNights}</th>}
            {!ownerView && <th scope="col">{TEXT.blockedNights}</th>}
            {!ownerView && <th scope="col">{TEXT.unknownNights}</th>}
            <th scope="col" className="lp-group-start">{TEXT.bookingsMadeShort}</th>
            <th scope="col">{TEXT.newReviewsShort}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const isSelected = selected === r.unitCode;
            const open = () => onSelect(isSelected ? null : r.unitCode);
            return (
              <tr
                key={r.unitCode}
                className={`lp-row${isSelected ? ' lp-row--selected' : ''}`}
                onClick={open}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                tabIndex={0}
                aria-expanded={isSelected}
                aria-label={`${TEXT.openDetail} ${r.unitCode}`}
              >
                <th scope="row"><span className="lp-unit-code">{r.unitCode}</span></th>
                <td data-label={TEXT.occupancy} className="lp-board__occ lp-group-start">
                  <OccupancyCell cell={ownerView ? { ...r.occupancy, target: null } : r.occupancy} />
                </td>
                <td data-label={TEXT.bookedNights} className="lp-group-start"><Figure value={r.bookedNights ?? null} quietZero /></td>
                {!ownerView && <td data-label={TEXT.openNights}><Figure value={r.openNights ?? null} quietZero /></td>}
                {!ownerView && <td data-label={TEXT.blockedNights}><Figure value={r.blockedNights ?? null} quietZero /></td>}
                {!ownerView && (
                  <td data-label={TEXT.unknownNights}>
                    <Figure value={r.unknownNights ?? null} quietZero />
                    {r.conflictNights > 0 && (
                      <Hint text={`${TEXT.conflictHint}: ${r.conflictNights}`}>
                        <i className="bi bi-exclamation-triangle lp-warn" aria-label={TEXT.conflictHint} />
                      </Hint>
                    )}
                  </td>
                )}
                <td data-label={TEXT.bookingsMadeShort} className="lp-group-start">
                  {!r.weekEnd && <Figure value={null} />}
                  {r.weekEnd && r.bookingsOnlyAirbnb && (
                    // An imported sheet week: only the Airbnb count was ever kept — the rest is unknown, not zero.
                    // A plain «—» inside: this hint already explains it — never two hints at once.
                    <Hint text={TEXT.bookingsOnlyAirbnb}>
                      <span>
                        {r.bookingsMade?.airbnb === null || r.bookingsMade?.airbnb === undefined
                          ? <span className="lp-missing">—</span>
                          : <>Airbnb <Figure value={r.bookingsMade.airbnb} quietZero /></>}
                        <span className="lp-muted"> *</span>
                      </span>
                    </Hint>
                  )}
                  {r.weekEnd && !r.bookingsOnlyAirbnb && (platformsText(r.bookingsMade) || <Figure value={0} quietZero />)}
                </td>
                <td data-label={TEXT.newReviewsShort}>
                  {r.newReviews ? (
                    <span className="lp-cell">
                      <Figure value={r.newReviews.count ?? null} quietZero />
                      {r.newReviews.avgStars !== null && r.newReviews.avgStars !== undefined && <span className="lp-muted">(<Figure value={r.newReviews.avgStars} kind="avg" />★)</span>}
                    </span>
                  ) : <Figure value={null} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AllPlatformsTable;
